import React, { ChangeEvent, FC, useEffect, useState } from 'react';

import Checkbox from 'components/common/ui-kit/Checkbox';
import EntranceExamsTable from 'components/statements/EntranceExamsForm/Table/view';
import useAppDispatch from 'hooks/common/useAppDispatch';
import useAppSelector from 'hooks/common/useAppSelector';
import terms from 'i18n';
import {
  SchoolTestType,
  UserSchoolTestResponse,
} from 'models/applications/school-tests';
import { createToast, showModal } from 'redux/actions';
import { apiSlice } from 'redux/api/api-slice';

import Button from '../../../components/common/ui-kit/Button';
import Loader from '../../../components/common/ui-kit/Loader';
import ModeratorDataList from '../../../components/moderator/ModeratorDataList';
import { useModeratorSchoolTests } from '../../../hooks/moderator/applications/useModeratorSchoolTests';
import { UserRole } from '../../../models/user';
import {
  useLazyGetAllTestsQuery,
  useUpdateSchoolTestMutation,
} from '../../../redux/api/applications/school-test-api';
import { selectUserRole } from '../../../redux/selectors';
import {
  selectCurrentApplication,
  selectCurrentApplicationUser,
} from '../../../redux/selectors/staff/applications';
import { importUserSchoolTests } from '../ModeratorStatementsPage/view';
const headers = [
  'Предмет',
  'ЦТ',
  'Балл',
  'Год сдачи',
  'Проверено',
  'Тип экзамена',
];

export const schoolTestTypeToString: { [key in SchoolTestType]: string } = {
  SECONDARY: 'ГИА',
  UPPER_SECONDARY: 'ЕГЭ',
};

function CentralTestingCheckbox({
  schoolTest,
  onCheckChange,
  onSuccess,
}: {
  schoolTest: UserSchoolTestResponse;
  onCheckChange?: (id: number, check: boolean) => void;
  onSuccess?: () => void;
}) {
  const initialChecked = schoolTest.isCentralTesting;
  const dispatch = useAppDispatch();
  const [isChecked, setIsChecked] = useState(initialChecked);
  const [update] = useUpdateSchoolTestMutation();

  const role = useAppSelector(selectUserRole);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setIsChecked(newValue);
    onCheckChange?.(schoolTest.id, newValue);
    update({
      id: schoolTest.id,
      subjectId: schoolTest.subject.id,
      year: schoolTest.year,
      score: schoolTest.score,
      isCentralTesting: newValue,
      centralTestingDocumentId: schoolTest.centralTestingDocumentId,
      oldDocumentRequest: schoolTest.oldDocumentResponse,
    })
      .unwrap()
      .then(() => {
        dispatch(createToast('Значение ЦТ изменено', 'success'));
        onSuccess?.();
      })
      .catch(() => {
        setIsChecked(!newValue);
        onCheckChange?.(schoolTest.id, !newValue);
        dispatch(
          createToast(
            'Во время изменения значения ЦТ произошла ошибка',
            'danger'
          )
        );
      });
  };

  return (
    <Checkbox
      checked={isChecked}
      onChange={handleChange}
      disabled={![UserRole.ADMIN, UserRole.MODERATOR].includes(role)}
    />
  );
}

const ModeratorExamResultPage: FC = () => {
  const dispatch = useAppDispatch();
  const application = useAppSelector(selectCurrentApplication);
  const user = useAppSelector(selectCurrentApplicationUser);
  const role = useAppSelector(selectUserRole);
  const [getTests, { data: tests }] = useLazyGetAllTestsQuery();
  const [checkTests, setCheckTests] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (tests) {
      const initial: Record<number, boolean> = {};
      tests.forEach(test => {
        initial[test.id] = test.isCentralTesting;
      });
      setCheckTests(initial);
    }
  }, [tests]);

  const handleCentralTestingChange = (testId: number, check: boolean) => {
    setCheckTests(prev => ({ ...prev, [testId]: check }));
  };

  const hasAnyCentralTesting = Object.values(checkTests).some(Boolean);

  const handleImportUserSchoolTests = () => {
    importUserSchoolTests(
      () => {
        dispatch(apiSlice.util.invalidateTags(['School tests']));
      },
      () => {
        dispatch(
          createToast(
            'Во время загрузки результатов ЕГЭ произошла ошибка',
            'danger'
          )
        );
      },
      user?.id
    );
  };

  const handleEditResults = () => {
    dispatch(
      showModal({
        name: 'EDIT_RESULTS_MODAL',
        data: {
          tests,
        },
      })
    );
  };

  const exportStudentSchoolTestResults = useModeratorSchoolTests(
    user?.id,
    user?.idWithPrefix
  );
  useEffect(() => {
    if (application) {
      getTests(application.id);
    }
  }, [application]);

  if (!application || !tests) {
    return <Loader />;
  }

  function getDataListFromTestResult(testResult: UserSchoolTestResponse) {
    return [
      {
        title: 'Тип документа:',
        label:
          (testResult.oldDocumentResponse?.documentType &&
            terms[testResult.oldDocumentResponse?.documentType]) ??
          '-',
      },
      {
        title: 'Серия:',
        label: testResult.oldDocumentResponse?.oldDocumentSerial ?? '-',
      },
      {
        title: 'Номер:',
        label: testResult.oldDocumentResponse?.oldDocumentNumber ?? '-',
      },
      {
        title: 'Фамилия:',
        label: testResult.oldDocumentResponse?.oldSecondName ?? '-',
      },
      {
        title: 'Имя:',
        label: testResult.oldDocumentResponse?.oldFirstName ?? '-',
      },
      {
        title: 'Отчество:',
        label: testResult.oldDocumentResponse?.oldMiddleName ?? '-',
      },
    ];
  }

  const checkPhrase = (test: UserSchoolTestResponse) => {
    if (test.isCentralTesting) return '-';
    else return test.confirmed ? 'Проверено' : 'Не проверено';
  };

  return (
    <div className="moderator-exam-result-page">
      <table className="moderator-exam-result-table">
        <thead>
          <tr>
            {headers.map(header => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tests.map((test, id) => (
            <>
              <tr key={id}>
                <td>{test.subject.title}</td>
                <td>
                  <CentralTestingCheckbox
                    schoolTest={test}
                    onCheckChange={handleCentralTestingChange}
                    onSuccess={() => {
                      if (application) getTests(application.id);
                    }}
                  />
                </td>
                <td>{test.score ?? '-'}</td>
                <td>{test.year}</td>
                <td className={test.confirmed ? 'success' : 'danger'}>
                  {checkPhrase(test)}
                </td>
                <td>{schoolTestTypeToString[test.testType]}</td>
              </tr>
              {test.oldDocumentResponse && (
                <tr>
                  <td>
                    <div className="moderator-old-document-info">
                      <h2>Данные предыдущего документа</h2>
                      <ModeratorDataList
                        list={getDataListFromTestResult(test)}
                      />
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
      <div className="moderator-exam-result-page__btn">
        {(role === UserRole.MODERATOR || role === UserRole.ADMIN) &&
          hasAnyCentralTesting && (
            <Button theme="success" loading={false} onClick={handleEditResults}>
              Редактировать результаты
            </Button>
          )}
        {role === UserRole.ADMIN && (
          <>
            <Button
              theme="success"
              loading={false}
              onClick={handleImportUserSchoolTests}
            >
              Проверить результаты
            </Button>
            <Button
              theme="success"
              loading={false}
              onClick={exportStudentSchoolTestResults}
            >
              Скачать CSV
            </Button>
          </>
        )}
      </div>
      <EntranceExamsTable
        applicationId={application.id}
        userId={application.userId}
      />
    </div>
  );
};

export default ModeratorExamResultPage;
